const { formatMoney, getElements, getElement } = window.BAO.Utils

/**
 * @typedef {Object} SelectedVariantOption
 *
 * @property {string} label
 * @property {string} value
 */

const selectors = {
  addToCart: '[data-product-add-to-cart]',
  addToCartText: '[data-product-add-to-cart-text]',
  backInStock: '[data-product-back-in-stock]',
  similarInMySize: '[data-similar-in-my-size]',
  quantity: '[data-product-quantity]',
  stockMessaging: '[data-product-stock-messaging]',
  form: '[data-product-form]',
  bundleForm: '[data-product-form-bundle]',
  bundleParent: '[data-bundle-items-parent]',
  price: '[data-product-price]',
  priceCompare: '[data-product-price-compare]',
  priceCompareText: '[data-product-price-compare-text]',
  sku: '[data-product-sku]',
  deliveryCountdown: '[data-product-delivery-countdown]',
  multipack: '[data-product-multipack]',
}

class Variant {
  constructor (data, element) {
    /**
     * @type {ShopifyVariant}
     * @public
     */
    this.data = data
    /**
     * @type {VariantOptions}
     * @public
     */
    this.element = element
    /**
     * @type {ProductFormBundleSet||false}
     * @public
     */
    this.isBundle = element.closest(selectors.bundleParent) !== null
    this.isMultipack = element.closest(selectors.multipack) !== null
  }

  render () {
    this.updateBundleItem()
    this.setAvailability()
    this.setPrice()
    this.updateInput()
  }

  setPrice () {
    unhidePriceEls()

    const priceElements = getElements(selectors.price)
    priceElements.forEach(el => {
      el.innerHTML = formatMoney(this.data.price)
      el.classList.toggle('prd-Detail_Price-sale', !!this.data.compare_at_price)
    })

    const priceCompareElements = getElements(selectors.priceCompare)
    priceCompareElements.forEach(
      el => (el.innerHTML = formatMoney(this.data.compare_at_price)),
    )

    if (!this.data.compare_at_price) {
      hidePriceCompareElements()
    }

    // Urgency message.
    const variantQuantity = getElement(
      `[variant-quantity][data-product-id="${this.data.product_id}"]`,
    )
    if (!variantQuantity) return

    variantQuantity.dataset.variantId = this.id
  }

  allSelectedAvailable () {
    const parent = this.element.closest(selectors.bundleParent)

    return [
      ...parent.querySelectorAll('[data-current-variant-available]'),
    ].every(
      el =>
        el.dataset.currentVariantAvailable === 'true' &&
        el.dataset.selectionMade === 'true',
    )
  }

  setAvailability () {
    // Bundles handle this separately. See ProductFormBundleSet.
    if (!this.isBundle) {
      getElements(selectors.addToCart).forEach(el => {
        el.setAttribute('aria-hidden', !this.data.available)
        el.disabled = !this.data.available
      })
    }

    getElements(selectors.quantity).forEach(el => {
      el.setAttribute('aria-hidden', !this.data.available)
      el.disabled = !this.data.available
    })

    getElements(selectors.backInStock).forEach(el => {
      // Update only the BIS for the relevant product.
      // There could be multiple BIS on a page when used on a bundle.
      if (this.isBundle) {
        const bundleProductIds = el.dataset.productId.split(',')

        if (!bundleProductIds.includes(this.data.product_id)) {
          return
        }
      }

      el.setAttribute('aria-hidden', this.data.available)
      el.dataset.variantId = this.data.id
    })

    getElements(selectors.similarInMySize).forEach(el => {
      el.setAttribute('aria-hidden', this.data.available)
    })

    getElements(selectors.stockMessaging).forEach(
      el =>
        (el.textContent = this.data.available
          ? window.theme.strings.inStock
          : window.theme.strings.outOfStock),
    )

    getElements(selectors.addToCartText).forEach(
      el =>
        (el.textContent = this.data.available
          ? window.theme.strings.addToCart
          : window.theme.strings.soldOut),
    )

    getElements(selectors.deliveryCountdown).forEach(el => {
      el.setAttribute('aria-hidden', !this.data.available)
      if (!this.data.available) {
        el.classList.add('prd-Delivery_Countdown-hide')
      } else {
        el.classList.remove('prd-Delivery_Countdown-hide')
      }
    })
  }

  updateInput () {
    const inputs = getElements(`form[action*="/cart/add"] [name="id"]`)

    inputs.forEach(input => {
      input.value = this.id
      input.dispatchEvent(new Event('change', { bubbles: true }))
    })
  }

  updateBundleItem () {
    if (!this.element.dataset?.currentVariantId) return

    this.element.dataset.currentVariantId = this.id
    this.element.dataset.currentVariantAvailable = this.data.available

    window.BAO.dispatchEvent('variantAvailableChange')
  }

  /**
   * @param {string} name
   * @param {string} value
   *
   * @returns {void}
   */
  updateIndicator (name, value) {
    if (!this.element.els['variant-indicator']?.exists) return

    const indicator = this.element.els['variant-indicator'].elements.find(
      el => el.dataset.name === name,
    )
    if (!indicator) return

    indicator.textContent = value

    // Reveal option title if on a bundle PDP.
    if (this.isBundle && this.element.els['option-title']?.exists) {
      this.element.els['option-title'].element.setAttribute(
        'aria-hidden',
        false,
      )
    }
  }

  setUrl () {
    window.history.replaceState(
      { path: this.url.toString() },
      '',
      this.url.toString(),
    )
  }

  setShareUrl () {
    const shareButtons = getElements('share-button')
    if (shareButtons.length === 0) return

    shareButtons.forEach(shareButton => {
      shareButton.updateUrl(this.url.toString())
    })
  }

  /**
   * @returns {number}
   */
  get id () {
    return this.data.id
  }

  /**
   * @returns {URL}
   */
  get url () {
    // eslint-disable-next-line compat/compat
    const params = new URLSearchParams(window.location.search)
    params.set('variant', this.id.toString())

    // eslint-disable-next-line compat/compat
    const url = new URL(window.location.href)
    url.search = params.toString()

    return url
  }
}

export class VariantOptions extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['data', 'option', 'option-choice']
  }

  constructor () {
    super()

    /**
     * @type {Variant[]}
     * @public
     */
    this.collectedVariants = [new Variant(this.defaultVariant, this)]

    this.isMultipack = this.closest(selectors.multipack) !== null
  }

  setupListeners () {
    super.setupListeners()

    this.handleMultipack = this.handleMultipack.bind(this)
    this.handleMultipackSubmit = this.handleMultipackSubmit.bind(this)
    this.onVariantChange = this.onVariantChange.bind(this)

    this.listeners.add(this, 'change', this.onVariantChange)

    if (this.isMultipack) {
      this.listeners.add(this, 'change', this.handleMultipack)
      this.listeners.add(
        getElement(selectors.form),
        'submit',
        this.handleMultipackSubmit,
      )
    }
  }

  disableDefaultValue (e) {
    if (e.target?.options) {
      e.target.options[0].disabled = true
    }
  }

  handleMultipackGiftBox () {
    const multipackGiftboxData = this.multipackGiftboxData

    if (!multipackGiftboxData) return true

    return !!multipackGiftboxData
      .flat()
      .find(giftBox => giftBox.available === true)
  }

  handleMultipack (e) {
    this.disableDefaultValue(e)

    const value = e.target.value
    const form = getElement(selectors.form)
    this.productId = getElement('input[name="id"]', { context: form }).value
    this.currentOption = {
      option: e.target.name,
      value,
    }

    const multipackVariants = this.multipackData
    const multipackVariantsChosen = multipackVariants
      .map(variants => variants.filter(variant => variant.option1 === value))
      .flat()
    const multipackVariantsNotAllAvailable = !!multipackVariantsChosen.find(
      variant => !variant.available,
    )

    this.outOfStock =
      multipackVariantsNotAllAvailable ||
      multipackVariants.length !== multipackVariantsChosen.length
    this.giftBoxHasStock = this.handleMultipackGiftBox()

    getElements(selectors.addToCart).forEach(el => {
      if (!this.giftBoxHasStock) {
        el.disabled = true
      } else {
        el.disabled = this.outOfStock
      }
    })

    getElements(selectors.addToCartText).forEach(el => {
      if (!this.giftBoxHasStock) {
        el.textContent = this.giftBoxHasStock
          ? window.theme.strings.addToCart
          : window.theme.strings.soldOut
      } else {
        el.textContent = this.outOfStock
          ? window.theme.strings.soldOut
          : window.theme.strings.addToCart
      }
    })

    if (this.outOfStock || !this.giftBoxHasStock) return

    this.multipackVariantsToAdd = multipackVariantsChosen.map(
      variant => variant.id,
    )

    if (this.multipackGiftboxData) {
      const flatData = this.multipackGiftboxData.flat()

      this.multipackVariantsToAdd.push(flatData[0].id)
    }
  }

  async handleMultipackSubmit (e) {
    e.preventDefault()

    const variantQuantity = getElement(selectors.quantity)
    const variantQuantityValue = getElement('.qty-Adjuster_Input', {
      context: variantQuantity,
    }).value

    await window.BAO.Cart.addItem({
      id: this.productId,
      quantity: Number(variantQuantityValue),
      properties: {
        _multipack_option: this.currentOption,
        _multipack_items: `${this.multipackVariantsToAdd.toString()}`,
      },
    })
  }

  onVariantChange (e) {
    const variant = this.getVariant()
    variant.updateIndicator(e.target.name, e.target.value)

    window.BAO.dispatchEvent(window.BAO.EVENTS.VARIANT.CHANGE, { variant })

    if (this.isMultipack) return

    this.dataset.selectionMade =
      this.currentVariant?.id !== undefined ? 'true' : 'false'

    variant.render()

    if (!this.currentVariant) {
      this.resetPage(variant)

      return
    }

    variant.setUrl()
    variant.setShareUrl()
  }

  /**
   * @param {Variant} variant
   *
   * @returns {void}
   */
  resetPage (variant) {
    window.history.replaceState(
      { path: window.location.pathname },
      '',
      window.location.pathname,
    )
  }

  /**
   * @param {ShopifyVariant} data
   *
   * @returns {Variant}
   */
  createVariant (data) {
    const variant = new Variant(data, this)
    this.collectedVariants.push(variant)

    return variant
  }

  /**
   * @returns {Variant}
   */
  getVariant () {
    if (!this.currentVariant) {
      return this.collectedVariants[0]
    }

    const existingVariant = this.collectedVariants.find(
      variant => variant.id === this?.currentVariant?.id,
    )
    if (existingVariant) return existingVariant

    return this.createVariant(this.currentVariant)
  }

  /**
   * @returns {SelectedVariantOption[]}
   */
  get selectedOptions () {
    this.throwError(`selectedOptions needs to be implemented on this class`)

    return []
  }

  /**
   * @returns {ShopifyVariant}
   */
  get defaultVariant () {
    return this.data[0]
  }

  /**
   * @returns {ShopifyVariant|undefined}
   */
  get currentVariant () {
    return this.data.find(variant => {
      return variant.options.every(
        (option, index) => this.selectedOptions[index].value === option,
      )
    })
  }

  /**
   * @returns {ShopifyVariant[]}
   */
  get data () {
    const originalData = JSON.parse(this.els.data.element.innerText)

    return originalData.map(variant => ({
      ...variant,
      product_id: this.els.data.element.dataset.productId,
    }))
  }

  get multipackData () {
    return this.els['multipack-data']?.elements.map(el => {
      return JSON.parse(el.innerText)
    })
  }

  get multipackGiftboxData () {
    return this.els['multipack-gift-data']?.elements.map(el => {
      return JSON.parse(el.innerText)
    })
  }
}

export class VariantRadios extends VariantOptions {
  get selectedOptions () {
    return this.els.option.elements.map(el => {
      const { name } = el.dataset
      const choice = getElements(this.els['option-choice'].selector, {
        context: el,
      }).find(el => el.checked)?.value

      return {
        label: name,
        value: choice,
      }
    })
  }
}

export class VariantSelects extends VariantOptions {
  get selectedOptions () {
    return this.els.option.elements.map(el => {
      const { name } = el.dataset

      return {
        label: name,
        value: el.querySelector('select').value,
      }
    })
  }
}

if (!customElements.get('variant-radios')) {
  customElements.define('variant-radios', VariantRadios)
}

if (!customElements.get('variant-selects')) {
  customElements.define('variant-selects', VariantSelects)
}

function hideElements (elements) {
  elements.forEach(el => (el.hidden = true))
}

function unhideElements (elements) {
  elements.forEach(el => (el.hidden = false))
}

function unhidePriceEls () {
  unhideElements(getElements(`${selectors.price}, ${selectors.priceCompare}`))
}

function hidePriceCompareElements () {
  hideElements(getElements(selectors.priceCompare))
}
